import * as React from 'react';
import { connect } from 'react-redux';
import * as userActions from '../../../redux/user/actions.user';
import MainHeader from '../../headers/MainHeader/MainHeader';
import InviteFriendsHeader from '../../pages/inviteFriends/InviteFriendsHeader';
import asyncDispatch from '../../../services/mini/asyncDispatch';
import ValidationForm from '../../forms/ValidationForm';
import isValidEmail from '../../../services/mini/isValidEmail';
import { Loader, LoaderSize, LoaderTheme } from '../../components/Loader/Loader';
import InvitedFriendSignedUpIcon from './InvitedFriendSignedUpIcon';
import FriendsSvg from './FriendsSvg';
import ToasterService from '../../../services/ToasterService';
import errorMessages from '../../../const/errorMessages';
import errorCodes from '../../../const/errorCodes';
import { IInvitedFriend } from '../../../models/IInvitedFriend';
import { IInviteFriendParam } from '../../../redux/user/saga.user';
import { IStore } from '../../../redux/initialState';

const validateEmail = ({ email }) => {
    if (!isValidEmail(email)) {
        return 'Enter valid email address';
    }
    if (!email) {
        return 'Enter an email address';
    }
    return null;
}

type InviteFriendsPageProps = {
    invitedFriends: IInvitedFriend[],
    earnedFreeInteractionsPerMonth: number,
    getInvitedFriendsInfo: () => Promise<void>,
    inviteFriend: ({ email }: IInviteFriendParam) => Promise<void>,
};

const InviteFriendsPage = ({
    earnedFreeInteractionsPerMonth,
    getInvitedFriendsInfo,
    inviteFriend
}: InviteFriendsPageProps) => {

    const [updateTs, setUpdateTs] = React.useState(null);
    const [friends, setFriends] = React.useState(null);

    const getInfo = () => {
        getInvitedFriendsInfo().then((invitedFriends) => {
            setFriends(invitedFriends);
        });
    }

    React.useEffect(() => {
        getInfo();
    }, []);

    const ok = React.useCallback(({ email }) => {
        const wasAlreadyInvitedMoreThanTwice = !!friends.find((friend) => friend.email === email && (+friend.emailCount >= 2));

        if (wasAlreadyInvitedMoreThanTwice) {
            ToasterService.showWarning({ title: errorMessages[errorCodes.invitationEmailWasResent] });
        } else {
            inviteFriend({ email }).then(() => {
                setUpdateTs(Date.now());
                setFriends([{ email, isNew: true }, ...friends]);
            }, () => {
                setUpdateTs(Date.now());
            }).finally(() => {
                setTimeout(() => {
                    getInfo();
                }, 2200);
            });
        }
    }, [friends]);

    const hasEarnedInteractions = earnedFreeInteractionsPerMonth;

    return (
        <div className="invite-friends-page">
            <MainHeader>
                <InviteFriendsHeader />
            </MainHeader>
            <div className="invite-friends-page__body">
                <div className="invite-friends-page__form">
                    <div className="invite-friends-page__form-title">Invite Friends!</div>
                    <div className="invite-friends-page__form-text">Invite friends to get free interactions.</div>
                    <FriendsSvg />
                    <ValidationForm
                        key={updateTs}
                        inputs={[
                            {
                                name: 'email',
                                label: 'Email',
                                value: '',
                                applyOnEnter: true,
                                rules: [validateEmail],
                                className: 'invite-friends-page__form-input'
                            }
                        ]}
                        buttons={[
                            {
                                key: 'ok',
                                label: 'Invite',
                                action: ok,
                                className: `action-btn light-blue-with-border`
                            }
                        ]}
                        enableActionBtnByDefault={true}
                        btnsClassName="invite-friends-page__form-btn"
                    />
                    <div className="invite-friends-page__friend-list">
                        {friends === null ?
                            <Loader
                                theme={LoaderTheme.Dark}
                                size={LoaderSize.Small}
                                appearWithFade={true} /> :
                            <React.Fragment>
                                {friends.length > 0 ?
                                    friends.map(({ email, status, isNew }) => {
                                        return <div
                                            key={email}
                                            className={`invite-friends-page__friend-list-item ${status} ${isNew && 'new'}`}>
                                            <div>{email}</div>
                                            <div className={`invite-friends-page__friend-list-item-status ${status}`}>
                                                {status === 'signed-up' ? 'Accepted' : 'Invited'}
                                                <InvitedFriendSignedUpIcon />
                                            </div>
                                        </div>;
                                    }) :
                                    <div
                                        className="invite-friends-page__friend-list-item">
                                        Please invite friends to see your friends list
                                    </div>
                                }
                            </React.Fragment>
                        }
                    </div>
                    <div className={`invite-friends-page__earned-title ${hasEarnedInteractions && 'has-earned-interactions'}`}>You earned  <b>{earnedFreeInteractionsPerMonth || 0}</b> more free interactions a month</div>
                    <div className="invite-friends-page__earned-text">Invite 1 more friend to get another 100 free interactions a month.</div>
                </div>
            </div>
        </div>
    );


}

function mapStateToProps({ user }: IStore) {
    const { invitedFriends, earnedFreeInteractionsPerMonth } = user;
    return {
        invitedFriends,
        earnedFreeInteractionsPerMonth
    };
}

function mapDispatchToProps(dispatch) {
    return {
        inviteFriend: (param) => asyncDispatch(userActions.inviteFriend, dispatch, param),
        getInvitedFriendsInfo: (param) => asyncDispatch(userActions.getInvitedFriendsInfo, dispatch, param),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(InviteFriendsPage);
